import { API_ROUTES } from "../../configuration/RoutesApi"
import { ERequestType } from "../../Enum/ERequestType"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { AdminGrowthInvestmentDashboard } from "../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentDashboard"
import { AdminGrowthInvestmentFilter } from "../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentFilter"
import { SaveAdminGrowthInvestment } from "../../Objects/AdminGrowthInvestment/SaveAdminGrowthInvestment"
import i18next from "i18next"

const toExclusiveEndDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) return value

  const [, year, month, day] = match
  const nextDayUtc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + 1))

  return `${nextDayUtc.toISOString().split("T")[0]}T00:00:00.001Z`
}

const buildQuery = (filter: AdminGrowthInvestmentFilter) => {
  const params = new URLSearchParams()

  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.append(key, key === "endDate" ? toExclusiveEndDate(String(value)) : String(value))
    }
  })

  const query = params.toString()

  return query
    ? `${API_ROUTES.ADMIN_GROWTH_INVESTMENT.GET_DASHBOARD}?${query}`
    : API_ROUTES.ADMIN_GROWTH_INVESTMENT.GET_DASHBOARD
}

export const useAdminGrowthInvestment = () => {
  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const handleGetDashboard = async (
    filter: AdminGrowthInvestmentFilter,
    onSuccess: (data: AdminGrowthInvestmentDashboard) => void,
  ) => {
    enableLoad()

    try {
      const response = await GetRequest<AdminGrowthInvestmentDashboard>(buildQuery(filter), ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess(new AdminGrowthInvestmentDashboard(response.data ?? {}))
    } catch (ex) {
      const message = ex instanceof Error ? ex.message : i18next.t("admin:growth_investment_load_error")

      setError(i18next.t("admin:growth_investment_error_title"), message)
    } finally {
      disableLoad()
    }
  }

  const handleCreate = async (request: SaveAdminGrowthInvestment, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.ADMIN_GROWTH_INVESTMENT.CREATE, request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (ex) {
      const message = ex instanceof Error ? ex.message : i18next.t("admin:growth_investment_save_error")

      setError(i18next.t("admin:growth_investment_error_title"), message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdate = async (id: number, request: SaveAdminGrowthInvestment, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.ADMIN_GROWTH_INVESTMENT.UPDATE(id), request, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (ex) {
      const message = ex instanceof Error ? ex.message : i18next.t("admin:growth_investment_update_error")

      setError(i18next.t("admin:growth_investment_error_title"), message)
    } finally {
      disableLoad()
    }
  }

  return { handleGetDashboard, handleCreate, handleUpdate }
}
